/**
 * Extract flight segments from booking response – handles FTD, API v1/v2, and various structures
 */
export function toFlightsArray(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val.filter((f) => f && typeof f === "object" && (f.depCode || f.depCName || f.depCity || f.flightNo || f.flightNumber));
  if (typeof val === "object") {
    const arr = Object.values(val).filter((f) => f && typeof f === "object" && (f.depCode || f.depCName || f.depCity || f.flightNo));
    return arr.length > 0 ? arr : (val.depCode || val.depCName || val.depCity || val.flightNo ? [val] : []);
  }
  return [];
}

export function getFlightsFromResponse(res, direction) {
  if (!res || typeof res !== "object") return [];
  const key = direction === "return" ? "Return" : "Onward";
  let raw =
    res?.Flights?.[key] ||
    res?.flights?.Flights?.[key] ||
    res?.flights?.[key] ||
    res?.[key] ||
    res?.data?.Flights?.[key] ||
    res?.data?.flights?.[key];
  if (!raw && Array.isArray(res?.results)?.[0]) {
    const r0 = res.results[0];
    raw = r0?.Flights?.[key] || r0?.flights?.[key] || r0?.[key];
  }
  if (!raw && Array.isArray(res?.results)) {
    for (const r of res.results) {
      raw = r?.Flights?.[key] || r?.flights?.[key] || r?.[key];
      if (raw) break;
    }
  }
  if (!raw && res?.Flights) {
    raw = Array.isArray(res.Flights) ? res.Flights.find((f) => f?.direction === key) : null;
  }
  return toFlightsArray(raw);
}

/** Get dep/arr city codes from a booking (for pairing round-trip legs) */
export function getRouteFromBooking(b) {
  const onward = getFlightsFromResponse(b?.response || {}, "onward");
  const ret = getFlightsFromResponse(b?.response || {}, "return");
  const flights = onward.length > 0 ? onward : ret;
  if (!flights.length) return null;
  const first = flights[0];
  const last = flights[flights.length - 1];
  const dep = first?.depCode || first?.depCName;
  const arr = last?.arrCode || last?.arrCName;
  return dep && arr ? { dep, arr } : null;
}

/** Group round-trip bookings: pair onward+return legs into single items */
export function groupRoundTripBookings(list) {
  if (!list?.length) return [];
  const rt = (list || []).filter((b) => b.tripType === "1" || b.tripType === 1);
  const others = (list || []).filter((b) => !(b.tripType === "1" || b.tripType === 1));
  const paired = new Set();
  const result = [];

  for (let i = 0; i < rt.length; i++) {
    if (paired.has(i)) continue;
    const a = rt[i];
    const rA = getRouteFromBooking(a);
    if (!rA?.dep || !rA?.arr) {
      result.push({ type: "single", booking: a });
      continue;
    }
    const tA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    let found = -1;
    for (let j = i + 1; j < rt.length; j++) {
      if (paired.has(j)) continue;
      const b = rt[j];
      const rB = getRouteFromBooking(b);
      if (!rB?.dep || !rB?.arr) continue;
      const tB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      const timeDiff = Math.abs(tA - tB);
      const isComplementary = rA.arr === rB.dep && rA.dep === rB.arr;
      const isCloseTime = timeDiff < 5 * 60 * 1000;
      if (isComplementary && isCloseTime) {
        found = j;
        break;
      }
    }
    if (found >= 0) {
      paired.add(i);
      paired.add(found);
      const b = rt[found];
      const tB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      const onward = tA <= tB ? a : b;
      const returnLeg = tA <= tB ? b : a;
      result.push({ type: "roundtrip", onward, return: returnLeg });
    } else {
      result.push({ type: "single", booking: a });
    }
  }

  return [...result, ...others.map((b) => ({ type: "single", booking: b }))];
}

/** Get route string from first and last segment */
export function getRouteFromSegments(segments) {
  if (!segments || segments.length === 0) return null;
  const first = segments[0];
  const last = segments[segments.length - 1];
  const dep = first?.depCName || first?.depCity || first?.depCode || first?.depAirport || first?.DepartureCityName;
  const arr = last?.arrCName || last?.arrCity || last?.arrCode || last?.arrAirport || last?.ArrivalCityName;
  if (!dep || !arr) return null;
  return { dep, arr, depCode: first?.depCode, arrCode: last?.arrCode };
}

/** Extract fare from response */
export function getFareFromResponse(res) {
  if (!res) return null;
  return (
    res?.Fare?.PublishedFare ??
    res?.Fare?.total?.total ??
    res?.Fare?.total ??
    res?.Fare?.OfferedFare ??
    res?.fare?.PublishedFare ??
    res?.fare?.total ??
    res?.flights?.Fare?.PublishedFare ??
    res?.flights?.Fare?.total?.total ??
    res?.flights?.Fare?.OfferedFare ??
    res?.flights?.fare?.PublishedFare ??
    res?.data?.Fare?.PublishedFare ??
    res?.data?.Fare?.total?.total ??
    null
  );
}

/** Format YYYYMMDD / YYYYMMDDHHMM to readable */
export function formatDateTime(dateTimeStr, timeStr) {
  if (!dateTimeStr) return { date: "N/A", time: timeStr || "N/A" };
  const str = String(dateTimeStr).replace(/\D/g, "");
  try {
    const year = str.substring(0, 4);
    const month = str.substring(4, 6);
    const day = str.substring(6, 8);
    if (!year || !month || !day) return { date: "N/A", time: timeStr || "N/A" };
    const date = new Date(`${year}-${month}-${day}`);
    const formattedDate = date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    let formattedTime = timeStr || "N/A";
    if (str.length >= 12) {
      const hour = str.substring(8, 10);
      const minute = str.substring(10, 12);
      formattedTime = `${hour}:${minute}`;
    }
    return { date: formattedDate, time: formattedTime };
  } catch {
    return { date: "N/A", time: timeStr || "N/A" };
  }
}
